import React from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Target, Heart, Users, Award, Shield, Lightbulb, TrendingUp, Zap, Star, CheckCircle } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: Target,
      title: 'Our Mission',
      description: 'To empower businesses of all sizes with accessible, professional trademark and brand protection services. We believe every entrepreneur deserves world-class legal support without complexity or excessive costs.',
      gradient: 'from-indigo-500 to-purple-600',
    },
    {
      icon: Heart,
      title: 'Client-First Approach',
      description: 'Your success is our priority. We provide personalized attention, transparent pricing, and dedicated support throughout your trademark journey.',
      gradient: 'from-purple-500 to-pink-600',
    },
    {
      icon: Shield,
      title: 'Integrity & Trust',
      description: 'We operate with complete transparency and honesty. Our straightforward process means no hidden fees, no surprises, and clear communication at every step.',
      gradient: 'from-cyan-500 to-blue-600',
    },
  ];

  const approach = [
    {
      icon: Users,
      title: 'Expert Guidance',
      description: 'Our team of trademark professionals brings extensive experience in intellectual property law. We stay current with USPTO regulations and industry best practices.',
    },
    {
      icon: Award,
      title: 'Proven Results',
      description: 'With a 98% success rate, we have helped thousands of businesses secure their trademarks. Our comprehensive approach minimizes rejections.',
    },
    {
      icon: Lightbulb,
      title: 'Innovation',
      description: 'We leverage technology to streamline the trademark process while maintaining the personal touch that complex legal matters require.',
    },
  ];

  const milestones = [
    { number: '10,000+', label: 'Trademarks Registered', icon: Star },
    { number: '98%', label: 'Success Rate', icon: TrendingUp },
    { number: '50', label: 'States Covered', icon: Shield },
    { number: '24/7', label: 'Support Available', icon: Zap },
  ];

  const whyUs = [
    'Experienced trademark professionals',
    'Transparent, upfront pricing',
    'Dedicated case managers',
    'Comprehensive trademark searches',
    'Fast turnaround times',
    'High approval rates',
    'Ongoing brand monitoring',
    'Expert legal support',
  ];

  return (
    <div>
      <Navigation />

      {/* Creative Hero Section */}
      <section className="relative min-h-[80vh] flex items-center bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 overflow-hidden" data-testid="about-hero">
        {/* Animated Background Blobs */}
        <div className="absolute top-20 left-10 w-96 h-96 bg-indigo-300 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob"></div>
        <div className="absolute top-40 right-10 w-96 h-96 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-20 left-1/2 w-96 h-96 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-4000"></div>

        <div className="container mx-auto px-4 py-20 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-semibold rounded-full mb-4">
                Our Story
              </div>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
                <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                  Protecting Brands
                </span>
                <br />
                <span style={{ color: 'var(--text-dark)' }}>Empowering Entrepreneurs</span>
              </h1>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                We are dedicated professionals committed to helping businesses protect their most valuable asset – their brand identity.
              </p>
            </div>

            <div className="relative">
              <div className="relative z-10">
                <img
                  src="https://images.unsplash.com/photo-1497366754035-f200968a6e72"
                  alt="Professional office"
                  className="rounded-3xl shadow-2xl transform hover:scale-105 transition-transform duration-500"
                />
              </div>
              {/* Floating Badge */}
              <div className="absolute -bottom-6 -right-6 bg-white p-8 rounded-2xl shadow-2xl">
                <div className="text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">10,000+</div>
                <div className="text-sm text-gray-600">Brands Protected</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Milestone Stats with Creative Design */}
      <section className="py-20 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {milestones.map((milestone, index) => {
              const Icon = milestone.icon;
              return (
                <div key={index} className="text-center group" data-testid={`milestone-${index}`}>
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-lg rounded-2xl mb-4 group-hover:bg-white/30 transition-all group-hover:scale-110">
                    <Icon className="text-white" size={40} />
                  </div>
                  <div className="text-5xl font-bold text-white mb-2">{milestone.number}</div>
                  <div className="text-white/90">{milestone.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Story Section with Creative Split Layout */}
      <section className="section-padding bg-white" data-testid="story-section">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left - Image */}
              <div className="order-2 lg:order-1">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-3xl transform rotate-3"></div>
                  <img
                    src="https://images.unsplash.com/photo-1552664730-d307ca884978"
                    alt="Team collaboration"
                    className="relative rounded-3xl shadow-2xl"
                  />
                </div>
              </div>

              {/* Right - Content */}
              <div className="order-1 lg:order-2">
                <span className="text-indigo-600 font-semibold text-sm uppercase tracking-wide">About Us</span>
                <h2 className="text-4xl sm:text-5xl font-bold mt-2 mb-6" style={{ color: 'var(--text-dark)' }}>
                  Our Story
                </h2>
                <div className="space-y-4 text-gray-700 leading-relaxed">
                  <p className="text-lg">
                    Orbit Marks was founded with a simple yet powerful vision: to make professional trademark services accessible to businesses of all sizes across the United States.
                  </p>
                  <p className="text-lg">
                    What started as a mission to simplify trademark registration has grown into a comprehensive brand protection service. Today, we help businesses not only register their trademarks but also protect their intellectual property through copyright registration, company formation, and ongoing brand monitoring.
                  </p>
                  <p className="text-lg">
                    We believe that every business deserves the opportunity to protect their brand identity without navigating confusing legal jargon or paying exorbitant fees.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section with Creative Cards */}
      <section className="section-padding bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50" data-testid="values-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-indigo-600 font-semibold text-sm uppercase tracking-wide">Core Values</span>
            <h2 className="text-4xl sm:text-5xl font-bold mt-2 mb-4" style={{ color: 'var(--text-dark)' }}>
              What We Stand For
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div key={index} className="group relative" data-testid={`value-${index}`}>
                  {/* Background gradient on hover */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${value.gradient} rounded-3xl opacity-0 group-hover:opacity-10 transition-opacity`}></div>
                  
                  <div className="relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2 border-2 border-gray-100 group-hover:border-indigo-200 h-full">
                    <div className={`w-16 h-16 bg-gradient-to-br ${value.gradient} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                      <Icon className="text-white" size={32} />
                    </div>
                    <h3 className="text-2xl font-bold mb-4" style={{ color: 'var(--text-dark)' }}>
                      {value.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">{value.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Approach Section with Zigzag Layout */}
      <section className="section-padding bg-white" data-testid="approach-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-indigo-600 font-semibold text-sm uppercase tracking-wide">Our Approach</span>
            <h2 className="text-4xl sm:text-5xl font-bold mt-2 mb-4" style={{ color: 'var(--text-dark)' }}>
              How We Deliver Excellence
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our proven methodology for successful trademark registration
            </p>
          </div>

          <div className="space-y-12 max-w-6xl mx-auto">
            {approach.map((item, index) => {
              const Icon = item.icon;
              const isEven = index % 2 === 0;
              
              return (
                <div
                  key={index}
                  className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-12 items-center`}
                  data-testid={`approach-${index}`}
                >
                  {/* Icon */}
                  <div className="lg:w-1/3">
                    <div className="w-32 h-32 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-3xl flex items-center justify-center mx-auto shadow-2xl transform hover:scale-110 hover:rotate-6 transition-all">
                      <Icon className="text-white" size={64} />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="lg:w-2/3">
                    <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-3xl p-8 border-2 border-indigo-100 hover:border-indigo-200 transition-all">
                      <h3 className="text-3xl font-bold mb-4" style={{ color: 'var(--text-dark)' }}>
                        {item.title}
                      </h3>
                      <p className="text-gray-600 text-lg leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="section-padding bg-gradient-to-br from-indigo-50 to-purple-50" data-testid="why-us-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-indigo-600 font-semibold text-sm uppercase tracking-wide">Why Us</span>
            <h2 className="text-4xl sm:text-5xl font-bold mt-2 mb-4" style={{ color: 'var(--text-dark)' }}>
              What Sets Us Apart
            </h2>
          </div>

          <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4">
            {whyUs.map((reason, index) => (
              <div key={index} className="flex items-center bg-white p-5 rounded-xl shadow-lg hover:shadow-xl transition-all border-2 border-gray-100 hover:border-indigo-200 group" data-testid={`reason-${index}`}>
                <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center mr-4 flex-shrink-0 group-hover:scale-110 transition-transform">
                  <CheckCircle className="text-white" size={20} />
                </div>
                <span className="text-gray-700 font-medium">{reason}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 overflow-hidden" data-testid="cta-section">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600"></div>
        
        {/* Animated Shapes */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>

        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl sm:text-5xl font-bold mb-6 text-white">
            Ready to Work Together?
          </h2>
          <p className="text-lg md:text-xl mb-8 max-w-2xl mx-auto text-white/90">
            Let us help you protect your brand and build a stronger business foundation.
          </p>
          <a
            href="/contact"
            className="inline-block px-10 py-5 bg-white text-indigo-600 font-bold rounded-xl hover:bg-gray-100 shadow-2xl transform hover:scale-105 transition-all"
            data-testid="cta-button"
          >
            Get Started Today
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
